import { useDesktopVendorAccess } from '@/hooks/useDesktopVendorAccess';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  X,
} from 'lucide-react';
import Avatar from '@/components/ui/Avatar';
import BrandLogo from '@/components/ui/BrandLogo';
import { APP_ROUTES, canAccessRoute, getSidebarGroups } from '@/config/navigation';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { useLogout } from '@/hooks/useLogout';
import { useAllProjects } from '@/hooks/useQueries';
import { useAppStore, useAuthStore } from '@/hooks/useStore';
import { cn } from '@/utils/cn';
import WorkspaceTree from './WorkspaceTree';

const PRIMARY_ROUTE_KEYS = ['home', 'inbox', 'myTasks', 'credentials', 'vendors'] as const;

export default function Sidebar() {
  const navigate = useNavigate();
  const { canAccess: vendorAccess } = useDesktopVendorAccess();
  const location = useLocation();
  const {
    sidebarOpen,
    sidebarCollapsed,
    closeSidebar,
    setSidebarOpen,
    toggleSidebarCollapsed,
    openSearch,
    notificationUnreadCount,
  } = useAppStore();
  const { user } = useAuthStore();
  const logout = useLogout();
  const projectsQuery = useAllProjects();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useDialogFocus({
    isOpen: sidebarOpen && isMobile,
    onClose: closeSidebar,
    initialFocusRef: mobileCloseRef,
  });

  const primaryRoutes = PRIMARY_ROUTE_KEYS.map((key) => APP_ROUTES[key]).filter((route) =>
    canAccessRoute(route.key, user?.role, { vendorAccess }),
  );
  const moreGroups = useMemo(
    () =>
      getSidebarGroups(user?.role, { vendorAccess })
        .map(({ group, items }) => ({
          group,
          items: items.filter(
            (item) =>
              !PRIMARY_ROUTE_KEYS.includes(item.key as (typeof PRIMARY_ROUTE_KEYS)[number]) &&
              item.key !== 'projects',
          ),
        }))
        .filter(({ items }) => items.length > 0),
    [user?.role, vendorAccess],
  );

  const compact = sidebarCollapsed && !isMobile;

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
  };

  const handleMoreToggle = () => {
    if (compact) {
      toggleSidebarCollapsed();
      setMoreOpen(true);
      return;
    }
    setMoreOpen((current) => !current);
  };

  useEffect(() => {
    if (window.matchMedia('(max-width: 1023px)').matches) closeSidebar();
  }, [location.pathname, closeSidebar]);

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 1023px)');
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
      setSidebarOpen(!event.matches);
    };
    mobileQuery.addEventListener('change', handleBreakpointChange);
    return () => mobileQuery.removeEventListener('change', handleBreakpointChange);
  }, [setSidebarOpen]);

  if (!sidebarOpen) return null;

  return (
    <>
      <div
        className="overlay-backdrop fixed inset-0 z-[55] lg:hidden"
        onClick={closeSidebar}
        aria-hidden="true"
      />

      <aside
        ref={sidebarRef}
        role={isMobile ? 'dialog' : undefined}
        aria-modal={isMobile ? true : undefined}
        aria-label={isMobile ? 'Navigation menu' : 'Workspace navigation'}
        tabIndex={isMobile ? -1 : undefined}
        data-collapsed={compact || undefined}
        className={cn(
          'sidebar-surface safe-area-panel z-[60] flex h-full flex-none flex-col overflow-hidden transition-[width] duration-200',
          'fixed inset-y-0 left-0 w-[calc(100vw-3rem)] max-w-[280px]',
          compact
            ? 'lg:static lg:w-[var(--layout-sidebar-collapsed)] lg:overflow-visible'
            : 'lg:static lg:w-[var(--layout-sidebar-expanded)] lg:max-w-none',
        )}
      >
        <div className={cn('flex min-h-[112px] items-center gap-1 px-6', compact && 'px-1')}>
          {!compact && <BrandLogo className="min-w-0 flex-1" />}
          <button
            ref={mobileCloseRef}
            type="button"
            onClick={closeSidebar}
            aria-label="Close menu"
            className="icon-button lg:hidden"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            aria-label={compact ? 'Expand sidebar' : 'Collapse sidebar'}
            className="icon-button hidden lg:inline-flex"
          >
            {compact ? (
              <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
            ) : (
              <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
            )}
          </button>
        </div>

        <div className={cn('px-6 pb-4 lg:hidden', compact && 'flex justify-center px-2')}>
          <button
            type="button"
            onClick={openSearch}
            aria-label="Search"
            className={cn(
              'sidebar-search flex w-full items-center gap-2 text-left',
              compact && 'icon-button w-9 justify-center rounded-full p-0',
            )}
          >
            <Search className="h-4 w-4 flex-none" strokeWidth={1.5} />
            {!compact && (
              <>
                <span className="min-w-0 flex-1 truncate">Search tasks, projects…</span>
                <kbd className="text-[10px] text-gray-400">⌘K</kbd>
              </>
            )}
          </button>
        </div>

        <nav aria-label="Main navigation" className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          <div className={cn('space-y-2 px-6 pb-6', compact && 'px-1')}>
            {primaryRoutes.map((item) => (
              <NavLink
                key={item.key}
                to={item.path}
                end={item.path === '/'}
                title={compact ? item.label : undefined}
                aria-label={item.label}
                className={({ isActive }) =>
                  cn(
                    isActive ||
                      (item.key === 'vendors' &&
                        /^\/(vendors|properties|service-types)(\/|$)/.test(location.pathname))
                      ? 'sidebar-link-active'
                      : 'sidebar-link',
                    compact && 'justify-center px-0',
                  )
                }
              >
                <item.icon className="h-5 w-5 flex-none" strokeWidth={1.7} />
                {!compact && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
                {!compact && item.key === 'inbox' && notificationUnreadCount > 0 && (
                  <span className="sidebar-badge">{Math.min(notificationUnreadCount, 99)}</span>
                )}
              </NavLink>
            ))}
          </div>

          <div className={cn('px-6 pb-6 pt-1', compact && 'px-1')}>
            <WorkspaceTree
              projects={projectsQuery.data?.projects ?? []}
              userId={user?.id}
              isLoading={projectsQuery.isLoading}
              compact={compact}
            />
          </div>

          <div className={cn('px-6 pb-4', compact && 'px-1')}>
            <button
              type="button"
              onClick={handleMoreToggle}
              aria-label="More"
              aria-expanded={moreOpen}
              className={cn('sidebar-link w-full', compact && 'justify-center px-0')}
            >
              <MoreHorizontal className="h-5 w-5 flex-none" strokeWidth={1.7} />
              {!compact && (
                <>
                  <span className="flex-1 text-left">More</span>
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', moreOpen && 'rotate-180')}
                    aria-hidden="true"
                  />
                </>
              )}
            </button>

            {moreOpen && !compact && (
              <div className="mt-2 space-y-4">
                {moreGroups.map(({ group, items }) => (
                  <div key={group}>
                    <p className="sidebar-section-header">{group}</p>
                    <div className="space-y-px">
                      {items.map((item) => (
                        <NavLink
                          key={item.key}
                          to={item.path}
                          className={({ isActive }) =>
                            isActive ? 'sidebar-link-active' : 'sidebar-link'
                          }
                        >
                          <item.icon className="h-5 w-5 flex-none" strokeWidth={1.5} />
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className={cn('p-6 pt-3', compact && 'p-1')}>
          {canAccessRoute('myTasks', user?.role) && (
            <button
              type="button"
              onClick={() => navigate('/my-tasks?create=1')}
              className={cn('btn-primary mb-3 w-full', compact && 'h-12 w-12 p-0')}
              aria-label="New task"
            >
              <Plus className="h-4 w-4 flex-none" strokeWidth={1.8} />
              {!compact && <span>New task</span>}
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-label="User menu"
              className={cn(
                'flex w-full items-center gap-3 rounded-lg p-1 text-left transition-colors hover:bg-gray-100/60',
                compact && 'justify-center',
              )}
            >
              <Avatar name={user?.name || 'User'} src={user?.avatar_url} size="md" />
              {!compact && (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium text-gray-900">{user?.name}</p>
                    <p className="truncate text-[11px] text-gray-500">
                      {user?.role
                        ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}`
                        : ''}
                      {user?.job_title ? ` · ${user.job_title}` : ''}
                    </p>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-gray-400 transition-transform',
                      userMenuOpen && 'rotate-180',
                    )}
                    strokeWidth={1.5}
                  />
                </>
              )}
            </button>

            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                <div
                  className={cn(
                    'popover absolute bottom-full z-50 mb-2 w-52 overflow-hidden py-1',
                    compact ? 'left-10' : 'left-0',
                  )}
                >
                  {canAccessRoute('settings', user?.role) && (
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate('/settings');
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Settings className="h-4 w-4 text-gray-400" strokeWidth={1.5} />
                      Account settings
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 border-t px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
